<template>
  <div class="email-verification-prompt">
    <!-- Email Verification Required Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div class="text-center">
          <!-- Email Icon -->
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            Email Verification Required
          </h3>
          
          <p class="text-sm text-gray-500 mb-6">
            Please verify your email address before continuing. We've sent a verification link to:
          </p>
          
          <p class="text-sm font-medium text-gray-900 mb-6">
            {{ userEmail }}
          </p>
          
          <!-- Actions -->
          <div class="space-y-3">
            <button
              @click="resendVerification"
              :disabled="resending || cooldownActive"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="resending" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ resendButtonText }}
            </button>
            
            <button
              @click="checkVerificationStatus"
              :disabled="checking"
              class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <svg v-if="checking" class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              I've verified my email
            </button>
            
            <button
              @click="logout"
              class="w-full text-sm text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              Sign out and try again
            </button>
          </div>
          
          <!-- Success/Error Messages -->
          <div v-if="message" class="mt-4 p-3 rounded-md" :class="messageClass">
            <p class="text-sm">{{ message }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiService } from '@/services/api/index.js';
import { useAuthStore } from '@/store/auth.js';

export default {
  name: 'EmailVerificationPrompt',
  props: {
    userEmail: {
      type: String,
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showModal: false,
      resending: false,
      checking: false,
      cooldownActive: false,
      cooldownSeconds: 0,
      cooldownInterval: null,
      message: '',
      messageType: 'info' // 'success', 'error', 'info'
    };
  },
  computed: {
    resendButtonText() {
      if (this.resending) return 'Sending...';
      if (this.cooldownActive) return `Resend in ${this.cooldownSeconds}s`;
      return 'Resend verification email';
    },
    messageClass() {
      const baseClasses = 'text-center';
      switch (this.messageType) {
        case 'success':
          return `${baseClasses} bg-green-50 text-green-800 border border-green-200`;
        case 'error':
          return `${baseClasses} bg-red-50 text-red-800 border border-red-200`;
        default:
          return `${baseClasses} bg-blue-50 text-blue-800 border border-blue-200`;
      }
    }
  },
  watch: {
    show(newVal) {
      this.showModal = newVal;
    }
  },
  mounted() {
    this.showModal = this.show;
  },
  beforeUnmount() {
    if (this.cooldownInterval !== null) {
      clearInterval(this.cooldownInterval);
    }
  },
  methods: {
    async resendVerification() {
      if (this.resending || this.cooldownActive) return;
      
      try {
        this.resending = true;
        this.message = '';
        
        await apiService.resendVerificationEmail(this.userEmail);
        
        this.message = 'Verification email sent! Please check your inbox.';
        this.messageType = 'success';
        
        // Start cooldown
        this.startCooldown();
        
      } catch (error) {
        console.error('Failed to resend verification email:', error);
        this.message = error.message || 'Failed to send verification email. Please try again.';
        this.messageType = 'error';
      } finally {
        this.resending = false;
      }
    },
    
    async checkVerificationStatus() {
      if (this.checking) return;
      this.checking = true;
      // Emit verified and let the parent retry login.
      // If the email is confirmed in the backend, login will succeed.
      // If not, login will fail and the parent will re-open this modal.
      this.$emit('verified');
      this.closeModal();
      this.checking = false;
    },
    
    logout() {
      const authStore = useAuthStore();
      authStore.logout();
      this.$emit('logout');
      this.closeModal();
    },
    
    closeModal() {
      this.showModal = false;
      this.$emit('close');
    },
    
    startCooldown() {
      this.cooldownActive = true;
      this.cooldownSeconds = 30; // 30 second cooldown
      
      this.cooldownInterval = setInterval(() => {
        this.cooldownSeconds--;

        if (this.cooldownSeconds <= 0) {
          this.cooldownActive = false;
          clearInterval(this.cooldownInterval);
          this.cooldownInterval = null;
        }
      }, 1000);
    }
  }
};
</script>

<style scoped>
/* Loading spinner animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
